import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPrinter, FiDownload, FiEye, FiMapPin, FiClock, FiCalendar, FiCreditCard } from 'react-icons/fi';
import toast from 'react-hot-toast';
import API from '../api/axios';
import { formatPrice, formatDateTime, ORDER_STATUS_MAP } from '../utils/constants';
import './OrderDetail.css';

const getProxyUrl = (url) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com')) {
    if (url.includes('/upload/proxy')) return url;
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base}/upload/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const getDownloadUrl = (url, filename) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com')) {
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base}/upload/proxy?url=${encodeURIComponent(url)}&download=true&filename=${encodeURIComponent(filename || 'file')}`;
  }
  return url;
};

const formatPageRange = (pages) => {
  if (!pages || !pages.length) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) end = sorted[i];
    else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancellationSeconds, setCancellationSeconds] = useState(60);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchOrderDetail();
    fetchCancellationPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCancellationPolicy = async () => {
    try {
      const { data } = await API.get('/print-rates');
      if (data.success && data.rates) {
        const rate = data.rates.find(r => r.key === 'order_cancellation_seconds');
        if (rate) setCancellationSeconds(Number(rate.value));
      }
    } catch (err) {
      console.error('Failed to fetch cancellation policy:', err);
    }
  };

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/orders/${id}`);
      if (data.success) {
        setOrder(data.order);
      } else {
        toast.error('Failed to load order detail');
        navigate('/orders');
      }
    } catch (error) {
      console.error('Failed to load order', error);
      toast.error(error.response?.data?.error || 'Failed to fetch order details');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  // Timer loop for countdown
  useEffect(() => {
    if (!order || order.status !== 'confirmed') {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const elapsed = (Date.now() - new Date(order.createdAt).getTime()) / 1000;
      const remaining = Math.max(0, Math.ceil(cancellationSeconds - elapsed));
      setTimeLeft(remaining);
      return remaining;
    };

    const initial = calculateTimeLeft();
    if (initial <= 0) return;

    const timer = setInterval(() => {
      const rem = calculateTimeLeft();
      if (rem <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [order, cancellationSeconds]);

  const handleCancelOrder = async () => {
    try {
      setCancelling(true);
      await API.put(`/orders/${order._id}/cancel`);
      toast.success('Order cancelled. Amount refunded to store credits.');
      fetchOrderDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="page order-detail-loading">
        <div className="spinner"></div>
        <p>Loading receipt...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page error-state">
        <div className="icon">❌</div>
        <p>Order not found</p>
        <button className="btn btn-primary" onClick={() => navigate('/orders')}>
          Back to Orders
        </button>
      </div>
    );
  }

  const statusInfo = ORDER_STATUS_MAP[order.status] || {};
  const itemsSubtotal = order.items.reduce((s, i) => s + (i.price * i.quantity), 0);

  // Extract all files for the Document Hub
  const printFiles = [];
  order.items.forEach(item => {
    const o = item.options || {};
    if (item.fileUrl && !o.frontFileUrl && !o.backFileUrl) {
      printFiles.push({
        name: item.name,
        url: item.fileUrl,
        type: item.type === 'print' ? 'Print Document' : 'Print Photo'
      });
    }
    if (o.frontFileUrl) {
      printFiles.push({
        name: `${item.name} (Front)`,
        url: o.frontFileUrl,
        type: 'PVC Card Front'
      });
    }
    if (o.backFileUrl) {
      printFiles.push({
        name: `${item.name} (Back)`,
        url: o.backFileUrl,
        type: 'PVC Card Back'
      });
    }
  });

  const timelineSteps = [
    { key: 'placed', label: 'Order Placed' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'processing', label: 'Processing' },
    { key: 'out-for-delivery', label: 'Out for Delivery' },
    { key: 'delivered', label: 'Delivered' }
  ];

  const currentStepIndex = (() => {
    const status = order.status;
    if (status === 'placed') return 0;
    if (status === 'confirmed' || status === 'contacted') return 1;
    if (status === 'processing') return 2;
    if (status === 'out-for-delivery') return 3;
    if (status === 'delivered') return 4;
    return -1; // cancelled
  })();

  return (
    <div className="page order-detail-page">
      {/* Header (Hidden in Print) */}
      <div className="page-header no-print">
        <button className="back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft />
        </button>
        <h1 className="page-title">Order Details</h1>
        <button className="btn btn-primary print-action-btn" onClick={handlePrint} title="Print or Save PDF">
          <FiPrinter style={{ marginRight: '6px' }} />
          <span className="print-btn-text-full">Print / Save PDF</span>
          <span className="print-btn-text-short">PDF</span>
        </button>
      </div>

      <div className="order-detail-layout">
        {/* Left Column: Retail Invoice Receipt */}
        <div className="invoice-left-column">
          <div className="invoice-container">
            {/* Invoice Header */}
            <div className="invoice-header">
              <div className="invoice-brand">
                <span className="brand-logo">🖨️</span>
                <div>
                  <h2 className="brand-name">Shubham Computers</h2>
                  <p className="brand-sub">Adarsh Nagar, Jhajjar, Haryana - 124103</p>
                  <p className="brand-contact">93503 36367 · shubhamcybercafe001@gmail.com</p>
                </div>
              </div>
              <div className="invoice-title-block">
                <h1 className="invoice-type">BILL OF PURCHASE</h1>
                <p className="invoice-number">Invoice: <strong>{order.orderId}</strong></p>
                <span className="invoice-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                  {statusInfo.label}
                </span>
              </div>
            </div>

            <div className="invoice-meta-grid">
              <div className="meta-card">
                <div className="meta-card-title"><FiCalendar size={13} /> Order Date</div>
                <div className="meta-card-value">{formatDateTime(order.createdAt)}</div>
              </div>
              <div className="meta-card">
                <div className="meta-card-title"><FiCreditCard size={13} /> Payment Method</div>
                <div className="meta-card-value">Store Credits (Paid)</div>
              </div>
              <div className="meta-card">
                <div className="meta-card-title"><FiClock size={13} /> Est. Delivery</div>
                <div className="meta-card-value">
                  {order.estimatedDelivery
                    ? `${formatDateTime(order.estimatedDelivery)}${order.isNextDayDelivery ? ' (Next Day Delivery)' : ''}`
                    : 'Immediate (Within 30 mins)'}
                </div>
              </div>
            </div>

            {/* Address & Customer Info (Visible in Print only, screen layout has it in the right column) */}
            <div className="invoice-section address-section print-only">
              <h3 className="section-title"><FiMapPin size={14} /> Delivery Information</h3>
              <div className="address-details">
                <p><strong>Customer Name:</strong> {order.customer?.name || 'Valued Client'}</p>
                <p><strong>Phone:</strong> +91 {order.customer?.phone || '93503 36367'}</p>
                <p>
                  <strong>Destination:</strong> {order.deliveryAddress?.full || order.deliveryAddress?.label || 'In-Store Handover / Pickup'}
                </p>
              </div>
            </div>

            {/* Purchase Items List */}
            <div className="invoice-section items-section">
              <h3 className="section-title">Purchased Items & Configurations</h3>
              <div className="items-table-header">
                <span>Item Description</span>
                <span style={{ textAlign: 'center' }}>Qty</span>
                <span style={{ textAlign: 'right' }}>Price</span>
              </div>
              <div className="items-table-rows">
                {order.items.map((item, idx) => {
                  const o = item.options || {};
                  return (
                    <div key={idx} className="items-table-row">
                      <div className="item-description-block">
                        <span className="item-main-name">{item.name}</span>
                        
                        {/* Print Specs */}
                        {item.type === 'print' && (
                          <div className="item-specs-subtext">
                            {[
                              o.copies > 1 ? `${o.copies} copies` : '1 copy',
                              o.pages ? `${o.pages} page${o.pages !== 1 ? 's' : ''}` : null,
                              o.pageSelection === 'range' && o.pageRange ? (typeof o.pageRange === 'string' ? `Pages ${o.pageRange}` : `Pages ${o.pageRange.start}-${o.pageRange.end}`) : null,
                              o.pageSelection === 'custom' && o.customPages ? `Pages ${formatPageRange(o.customPages)}` : null,
                              o.color === 'bw' ? 'Black & White' : o.color === 'colour' ? 'Colour' : o.color || null,
                              o.sides === 'single' ? 'One side' : o.sides === 'both' ? 'Both sides' : o.sides || null,
                              o.orientation || null,
                              o.scale ? `Scale: ${o.scale}%` : null
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}

                        {/* Print Photo Specs */}
                        {item.type === 'mart' && o.passportPack && (
                          <div className="item-specs-subtext">
                            {[
                              o.passportPack,
                              o.passportSize,
                              o.passportBg ? `${o.passportBg} background` : null,
                              o.extraCopies > 0 ? `+${o.extraCopies * 8} extra sheets` : null
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}

                        {/* PVC Card Details */}
                        {item.type === 'mart' && (o.service || o.frontFileUrl || o.backFileUrl || o.lostDescription) && (
                          <div className="item-specs-subtext lost-details-wrap">
                            {o.lostDescription && (
                              <div className="lost-details-desc">
                                <strong>Details:</strong> {o.lostDescription}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="item-qty-val">{item.quantity}</div>
                      <div className="item-price-val">{formatPrice(item.price * item.quantity)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Invoice Footer Breakdown */}
            <div className="invoice-section summary-section">
              <div className="summary-breakdown">
                <div className="summary-row">
                  <span className="summary-label">Items Subtotal</span>
                  <span className="summary-val">{formatPrice(itemsSubtotal)}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Delivery &amp; Handling</span>
                  <span className={`summary-val ${ (order.deliveryFee || 0) === 0 ? 'free-tag' : ''}`}>
                    {(order.deliveryFee || 0) === 0 ? 'FREE' : formatPrice(order.deliveryFee)}
                  </span>
                </div>
                <div className="summary-row grand-total-row">
                  <span className="summary-label">Grand Total Paid</span>
                  <span className="summary-val accent-val">{formatPrice(order.amount)}</span>
                </div>
              </div>
            </div>

            {/* Legal Disclaimers / In-Store Handover Rules */}
            <div className="invoice-footer">
              <p className="footer-title">Thank you for your purchase!</p>
              <p className="footer-terms">This is a system-generated digital invoice of purchase validated securely using your verified account credentials. For support or returns, visit us at our main Bazar branch or contact support on +91 93503 36367.</p>
            </div>
          </div>
        </div>

        {/* Right Column: Order Progress, Delivery details & Document Hub (Hidden in print) */}
        <div className="invoice-right-column no-print">
          {/* Order Progress Timeline */}
          <div className="detail-sidebar-card">
            <h3 className="sidebar-card-title">Order Status</h3>
            {order.status === 'cancelled' ? (
              <div className="order-cancelled-notice">
                <span className="cancel-icon">❌</span>
                <div>
                  <strong>Order Cancelled</strong>
                  <p>This order has been cancelled and refunded to your store credits.</p>
                </div>
              </div>
            ) : (
              <div className="timeline-tracker">
                {timelineSteps.map((step, idx) => {
                  const isCompleted = idx <= currentStepIndex;
                  const isActive = idx === currentStepIndex;
                  return (
                    <div key={step.key} className={`timeline-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}>
                      <div className="timeline-dot-wrapper">
                        <div className="timeline-dot">
                          {isCompleted ? '✓' : idx + 1}
                        </div>
                        {idx < timelineSteps.length - 1 && <div className="timeline-line" />}
                      </div>
                      <div className="timeline-label-group">
                        <span className="timeline-label">{step.label}</span>
                        {isActive && <span className="timeline-pulse-dot" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {order.status === 'confirmed' && timeLeft > 0 && (
              <div className="order-cancellation-prompt">
                <div className="cancel-timer-title">
                  <FiClock className="pulse-timer" />
                  <span>Cancel Window Open ({timeLeft}s)</span>
                </div>
                <p className="cancel-timer-desc">
                  You can cancel this order within the next {timeLeft} seconds for an immediate refund to your store credits.
                </p>
                <button 
                  className="cancel-order-btn" 
                  onClick={handleCancelOrder} 
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>
            )}
          </div>

          {/* Delivery & Receiver details */}
          <div className="detail-sidebar-card">
            <h3 className="sidebar-card-title"><FiMapPin size={14} /> Delivery Details</h3>
            <div className="delivery-details-sidebar">
              <div className="detail-row">
                <span className="detail-label">Receiver Name</span>
                <span className="detail-value">{order.deliveryAddress?.receiverName || order.customer?.name || 'Valued Client'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contact Phone</span>
                <span className="detail-value">+91 {order.customer?.phone || '93503 36367'}</span>
              </div>
              {order.deliveryAddress?.alternatePhone && (
                <div className="detail-row">
                  <span className="detail-label">Alternate Phone</span>
                  <span className="detail-value">+91 {order.deliveryAddress.alternatePhone}</span>
                </div>
              )}
              <div className="detail-row address-row">
                <span className="detail-label">Destination</span>
                <span className="detail-value">{order.deliveryAddress?.full || order.deliveryAddress?.label || 'In-store Pickup'}</span>
              </div>
            </div>
          </div>

          {/* Document Hub */}
          <div className="detail-sidebar-card">
            <h3 className="sidebar-card-title">📂 Document Hub</h3>
            {printFiles.length === 0 ? (
              <p className="no-docs-text">No document uploads required for this order.</p>
            ) : (
              <div className="document-hub-list">
                {printFiles.map((file, idx) => (
                  <div key={idx} className="document-hub-item">
                    <div className="doc-icon-title">
                      <span className="doc-type-icon">📄</span>
                      <div className="doc-meta-info">
                        <span className="doc-name">{file.name}</span>
                        <span className="doc-type-tag">{file.type}</span>
                      </div>
                    </div>
                    <div className="doc-actions">
                      <a href={getProxyUrl(file.url)} target="_blank" rel="noopener noreferrer" className="btn btn-outline preview-btn">
                        <FiEye size={12} /> Preview
                      </a>
                      <a href={getDownloadUrl(file.url, file.name)} className="btn btn-primary download-btn">
                        <FiDownload size={12} /> Download
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ height: 80 }} className="no-print" />
    </div>
  );
};

export default OrderDetail;
